import { common } from '@gosupersimple/types';

import { omit } from 'lodash';
import { useState, useEffect, useMemo } from 'react';

import { SelectOptions } from '@/components/form/select';
import {
  getBigNumberComparisonDefaults,
  getBigNumberFields,
  isBigNumberWithComparison,
  setBigNumberOptions,
} from '@/explore/components/visualisation/utils';
import { createFieldOption } from '@/explore/components/select-options/field';
import { SortingEditor } from '@/explore/edit-pipeline/sorting-editor';
import { Toggle } from '@/components/form/toggle';
import { getTimeAggregationPeriodLabel } from '@/explore/pipeline/format';
import { Icon } from '@/components/icon';
import { InlineButton } from '@/components/button';
import { Tooltip } from '@/components/tooltip';
import { SearchInput } from '@/components/form/search-input';

import { Field, Visualisation, VisualisationViewOptions, TimeAggregationPeriod } from '../../types';

import formStyles from '../../../components/form/form.module.scss';
import styles from './styles.module.scss';

interface EditBigNumberVisualisationProps {
  visualisation: Visualisation;
  fields: Field[];
  visualisationFields: Field[];
  onChange: (options: Visualisation) => void;
}

interface ComparisonOptionsProps {
  viewOptions: VisualisationViewOptions;
  bigNumberFieldKey: string;
  visualisationFields: Field[];
  mainAxisPrecision: TimeAggregationPeriod | undefined;
  handleComparisonChange: (newOptions: Partial<VisualisationViewOptions['bigNumber']>) => void;
  handleRemoveComparison: () => void;
  isNewlyAdded?: boolean;
}

const ComparisonOptions = ({
  viewOptions,
  bigNumberFieldKey,
  visualisationFields,
  mainAxisPrecision,
  handleComparisonChange,
  handleRemoveComparison,
  isNewlyAdded = false,
}: ComparisonOptionsProps) => {
  const [isExpanded, setIsExpanded] = useState(isNewlyAdded);

  // Get the current comparison target label
  const comparisonTargetLabel = useMemo(() => {
    if (viewOptions.bigNumber && 'comparisonKey' in viewOptions.bigNumber) {
      const currentKey = viewOptions.bigNumber.comparisonKey;
      const isPeriodToDate = viewOptions.bigNumber.comparisonMode === 'period_to_date';

      if (currentKey === bigNumberFieldKey) {
        if (mainAxisPrecision !== undefined) {
          const periodLabel = getTimeAggregationPeriodLabel(mainAxisPrecision).toLowerCase();
          return isPeriodToDate ? `previous ${periodLabel} to date` : `previous ${periodLabel}`;
        }
        return 'second row of the same column';
      }
      const field = visualisationFields.find((f) => f.key === currentKey);
      return field?.name ?? currentKey;
    }
    return '';
  }, [viewOptions.bigNumber, bigNumberFieldKey, mainAxisPrecision, visualisationFields]);

  if (!isBigNumberWithComparison(viewOptions?.bigNumber)) {
    return null;
  }

  const getBigNumberComparisonOptions = (): SelectOptions => {
    const otherFields = getBigNumberFields(visualisationFields)
      .map(createFieldOption)
      .filter((field) => field.value !== bigNumberFieldKey);

    const options: SelectOptions = [
      {
        label:
          mainAxisPrecision !== undefined
            ? `Previous ${getTimeAggregationPeriodLabel(mainAxisPrecision).toLowerCase()}`
            : 'Second row of the same column',
        value: bigNumberFieldKey,
      },
    ];

    if (otherFields.length > 0) {
      options.push({
        label: 'Other fields (same row)',
        options: otherFields,
      });
    }

    return options;
  };

  return (
    <div className={styles.comparisonContainer}>
      <div className={styles.comparisonHeader} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={styles.comparisonLabel}>
          <span>Compare with&nbsp;</span>
          <span className={styles.comparisonTarget}>{comparisonTargetLabel}</span>
        </div>
        <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
      </div>
      {isExpanded && (
        <div className={styles.comparisonContent}>
          <SearchInput
            value={viewOptions.bigNumber.comparisonKey}
            options={getBigNumberComparisonOptions()}
            closeOnSelect
            onChange={(key) => {
              handleComparisonChange({
                comparisonKey: key,
                comparisonMode: 'value',
              });
            }}
          />
          {mainAxisPrecision !== undefined && (
            <Toggle
              checked={(viewOptions.bigNumber.comparisonMode ?? 'value') === 'period_to_date'}
              onChange={(checked) => {
                handleComparisonChange({
                  comparisonMode: checked ? 'period_to_date' : 'value',
                });
              }}
              size="small">
              {getTimeAggregationPeriodLabel(mainAxisPrecision)} to date
            </Toggle>
          )}
          <Toggle
            checked={viewOptions.bigNumber.comparisonDirection === 'lower'}
            onChange={(state) =>
              handleComparisonChange({
                comparisonDirection: state ? 'lower' : 'higher',
              })
            }
            size="small">
            Lower value is better
          </Toggle>
          <Toggle
            checked={viewOptions.bigNumber?.comparisonType === 'percentage'}
            onChange={(state) =>
              handleComparisonChange({
                comparisonType: state ? 'percentage' : 'absolute',
              })
            }
            size="small">
            Display comparison as percentage
          </Toggle>
          <Toggle
            checked={viewOptions.bigNumber.showComparisonLabel ?? false}
            onChange={(state) => handleComparisonChange({ showComparisonLabel: state })}
            size="small">
            Display comparison field label
          </Toggle>
          <InlineButton size="small" onClick={handleRemoveComparison}>
            Remove
          </InlineButton>
        </div>
      )}
    </div>
  );
};

export const EditBigNumberVisualisation = (props: EditBigNumberVisualisationProps) => {
  const { visualisation, fields, visualisationFields, onChange } = props;
  const [justAddedComparison, setJustAddedComparison] = useState(false);

  // Reset the flag after the comparison options are rendered
  useEffect(() => {
    if (justAddedComparison) {
      setJustAddedComparison(false);
    }
  }, [justAddedComparison]);

  const viewOptions = visualisation.viewOptions ?? {};
  const bigNumberOptions = viewOptions.bigNumber;
  const bigNumberFieldKey = bigNumberOptions?.key;

  const mainAxisPrecision = visualisationFields.find(
    (field) => field.key === visualisation.mainAxisKey,
  )?.precision;

  const sorting = bigNumberOptions?.sort;
  const bigNumberComparisonKey =
    bigNumberOptions && 'comparisonKey' in bigNumberOptions
      ? bigNumberOptions?.comparisonKey
      : undefined;

  const handleChange = (newOptions: VisualisationViewOptions['bigNumber']) => {
    onChange({
      ...visualisation,
      viewOptions: setBigNumberOptions({ bigNumber: newOptions }, visualisation.viewOptions, {
        mainAxisKey: visualisation.mainAxisKey,
      }),
    });
  };

  const handleComparisonToggleChange = (checked: boolean) => {
    if (bigNumberOptions?.key === undefined) {
      throw new Error('Invalid big number state');
    }

    if (checked) {
      setJustAddedComparison(true);
    }

    handleChange(
      !checked
        ? omit(bigNumberOptions, 'comparisonKey')
        : {
            ...bigNumberOptions,
            ...getBigNumberComparisonDefaults(bigNumberOptions.key),
          },
    );
  };
  const handleSortingChange = (sort: common.Sorting[]) => {
    if (bigNumberOptions?.key === undefined) {
      throw new Error('Invalid big number state');
    }
    handleChange({ ...bigNumberOptions, sort });
  };

  const handleComparisonChange = (newOptions: Partial<VisualisationViewOptions['bigNumber']>) => {
    if (!isBigNumberWithComparison(viewOptions?.bigNumber)) {
      return;
    }

    const optionsToUpdate: VisualisationViewOptions['bigNumber'] = {
      ...viewOptions.bigNumber,
      ...newOptions,
    };

    return handleChange(optionsToUpdate);
  };
  return (
    <div className={formStyles.formHorizontal}>
      {bigNumberFieldKey !== undefined ? (
        <div className={formStyles.formRow}>
          {sorting !== undefined && <label className={formStyles.formLabel}>Show</label>}
          <SearchInput
            value={bigNumberFieldKey}
            closeOnSelect
            options={getBigNumberFields(visualisationFields).map(createFieldOption)}
            onChange={(key) => handleChange({ key })}
          />
        </div>
      ) : null}
      {sorting !== undefined && (
        <div className={formStyles.formRow}>
          <label className={formStyles.formLabel}>sorted by</label>
          <SortingEditor
            fields={fields}
            sorting={sorting ?? []}
            setSorting={(sort) => {
              handleSortingChange(sort);
            }}
          />
        </div>
      )}
      {bigNumberFieldKey !== undefined ? (
        <>
          {bigNumberComparisonKey !== undefined && (
            <ComparisonOptions
              viewOptions={viewOptions}
              bigNumberFieldKey={bigNumberFieldKey}
              visualisationFields={visualisationFields}
              mainAxisPrecision={mainAxisPrecision}
              handleComparisonChange={handleComparisonChange}
              handleRemoveComparison={() => handleComparisonToggleChange(false)}
              isNewlyAdded={justAddedComparison}
            />
          )}
          <div>
            {bigNumberComparisonKey === undefined ? (
              <InlineButton size="small" onClick={() => handleComparisonToggleChange(true)}>
                <Icon name="Plus" size={15} /> Comparison
              </InlineButton>
            ) : (
              <Tooltip content="Only one comparison is currently supported">
                <InlineButton size="small" disabled>
                  <Icon name="Plus" size={15} /> Comparison
                </InlineButton>
              </Tooltip>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};
